// Fetch timeout (30s)
const FETCH_TIMEOUT = 30 * 1000;

/**
 * @public
 */
export enum YonduOAuthInteractionQueryParams {
  AUTHORIZATION = 'authorization',
  INTERACTION = 'interaction',
  START_URI = 'oauth_interaction_start_uri',
  FINALIZE_URI = 'oauth_interaction_finalize_uri',
  RESUME_URI = 'oauth_authorization_resume_uri',
}

/**
 * @public
 */
export interface YonduOAuthInteractionURIs {
  startUri: string;
  finalizeUri: string;
  resumeUri: string;
}

/**
 * The interaction state as served by the OAuth adapter.
 * - `Active`: the interaction is awaiting a response from the user agent.
 * - `Responded`: the user agent has already responded (e.g. a re-served step after a refresh).
 * @public
 */
export type YonduOAuthInteractionState = 'Active' | 'Responded';

/**
 * @public
 */
export interface YonduOAuthInteractionContext<
  TContextRequest extends Record<string, unknown> = Record<string, unknown>,
> {
  uuid: string;
  name: string;
  state: YonduOAuthInteractionState;
  interaction_uri: string;
  request: TContextRequest;
  /**
   * Adapter-owned, delivery-mode-namespaced config projected onto the served step.
   */
  deliveryModeSpecificConfig?: {
    oauth?: {
      restartUri: string;
    };
    embedded?: {
      allowedEmbedOrigins: string[];
    };
  };
}

/**
 * OAuth error
 * @public
 */
export interface YonduOAuthErrorResponse {
  error: string;
  error_description: string;
}

/**
 * OAuth error
 * @public
 */
export class YonduOAuthError extends Error {
  protected _type: string;
  protected _description: string;

  constructor(type: string, description: string) {
    super(`${type}: ${description}`);

    this._type = type;
    this._description = description;
  }

  /**
   * Gets error type
   */
  get type() {
    return this._type;
  }

  /**
   * Gets error description
   */
  get description() {
    return this._description;
  }
}

/**
 * @public
 */
export class YonduOAuthDissectLocationError extends Error {
  //
}

/**
 * @public
 */
export class YonduOAuthInteraction {
  /**
   * The URL used to start (activate) the interaction and retrieve its context.
   */
  private readonly startUri: string;

  /**
   * The URL used to finalize (settle) the interaction.
   */
  private readonly finalizeUri: string;

  /**
   * The URL used to resume (advance) the authorization flow after settlement.
   */
  private readonly resumeUri: string;

  /**
   * An Interaction client that helps to resolve a received interaction.
   *
   * @param source Either the url to dissect and obtain the start-, finalize-, and resumeUri (uses `window.url` by default when none is given), or already-resolved URIs.
   */
  constructor(source?: URL | YonduOAuthInteractionURIs) {
    const resolved = source ?? new URL(window.location.toString());
    const uris = resolved instanceof URL ? this.dissectURL(resolved) : resolved;

    this.startUri = uris.startUri;
    this.finalizeUri = uris.finalizeUri;
    this.resumeUri = uris.resumeUri;
  }

  /**
   * Builds an interaction from already-resolved values (e.g. an embedded-mode
   * JSON body) rather than a URL. The `authorization` and `interaction` values
   * are appended as query params onto each URI, mirroring the URL dissection.
   * @public
   */
  static fromURIs(params: {
    authorization: string;
    interaction: string;
    startUri: string;
    finalizeUri: string;
    resumeUri: string;
  }): YonduOAuthInteraction {
    const uriWithParams = (uri: string) => {
      const url = new URL(uri);
      url.searchParams.set('authorization', params.authorization);
      url.searchParams.set('interaction', params.interaction);
      return url.toString();
    };

    return new YonduOAuthInteraction({
      startUri: uriWithParams(params.startUri),
      finalizeUri: uriWithParams(params.finalizeUri),
      resumeUri: uriWithParams(params.resumeUri),
    });
  }

  /**
   * Start the interaction: activates the selected interaction (`Created → Active`) and returns its context.
   */
  async start<T extends YonduOAuthInteractionContext>(): Promise<T> {
    const response = await fetch(this.startUri, {
      cache: 'no-cache',
      method: 'post',
      signal: AbortSignal.timeout(FETCH_TIMEOUT),
    });
    const body = await response.json();

    if (!response.ok) {
      const e = body as YonduOAuthErrorResponse;
      throw new YonduOAuthError(e.error, e.error_description);
    }

    return body;
  }

  /**
   * Finalize the interaction: posts the decision data, settles the active interaction, and returns an ack. Navigate to `getResumeUri()` next.
   */
  async finalize(input: Record<string, unknown> = {}): Promise<void> {
    const response = await fetch(this.finalizeUri, {
      cache: 'no-cache',
      body: JSON.stringify(input),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'post',
      signal: AbortSignal.timeout(FETCH_TIMEOUT),
    });

    const body = await response.json();

    if (!response.ok) {
      const e = body as YonduOAuthErrorResponse;
      throw new YonduOAuthError(e.error, e.error_description);
    }
  }

  /**
   * Get the resume URI: the endpoint the user agent should navigate to after
   * finalizing. It advances the authorization flow and redirects the browser to
   * the client `redirect_uri`.
   */
  getResumeUri(): string {
    return this.resumeUri;
  }

  /**
   * Dissects url and obtains the start-, finalize-, and resumeUri.
   */
  private dissectURL(url: URL): YonduOAuthInteractionURIs {
    const search = new URLSearchParams(url.search);

    for (const uri of Object.values(YonduOAuthInteractionQueryParams)) {
      if (!search.has(uri)) {
        throw new YonduOAuthDissectLocationError(
          `URL is missing query param ${uri}`,
        );
      }
    }

    const authorization = search.get(
      YonduOAuthInteractionQueryParams.AUTHORIZATION,
    ) as string;
    const interaction = search.get(
      YonduOAuthInteractionQueryParams.INTERACTION,
    ) as string;

    const urlWithParams = (param: string) => {
      const url = new URL(search.get(param) as string);
      url.searchParams.set('authorization', authorization);
      url.searchParams.set('interaction', interaction);
      return url.toString();
    };

    return {
      startUri: urlWithParams(YonduOAuthInteractionQueryParams.START_URI),
      finalizeUri: urlWithParams(YonduOAuthInteractionQueryParams.FINALIZE_URI),
      resumeUri: urlWithParams(YonduOAuthInteractionQueryParams.RESUME_URI),
    };
  }
}
